import { MapComponent } from "@inovitas/infra3dsdk/dist/types/mm";
import { LidarFragment } from "@inovitas/infra3dsdk/dist/types/mm/component/map/LidarLayer";
import { Close, DeleteOutline } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Modal,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ClientConfig from "shared/config";
import PointcloudImage from "shared/assets/pointcloudexport_processed.png";
import { useSnackbar } from "shared/common/contexts/SnackbarProvider";
import useAuth from "shared/common/hooks/UseAuth";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import PopoverElement from "shared/common/utils/popover/PopoverElement";
import {
  getAuthorizationHeaders,
  getApiKeyHeaders,
} from "shared/common/utils/general-utils";

function PointCloudExport() {
  const apm = defaultStore((state) => state.apm);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const showPCLExport = defaultStore((state) => state.showPCLExport);
  const changeShowPCLExport = defaultStore(
    (state) => state.changeShowPCLExport
  );
  const changeShowStreetMap = defaultStore(
    (state) => state.changeShowStreetMap
  );
  const viewer = defaultStore((state) => state.viewer);
  const showMapWindow = defaultStore((state) => state.showMapWindow);
  const projectConfig = defaultStore((state) => state.projectConfig);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const changeLoading = defaultStore((state) => state.changeLoading);

  const { userData, tokens } = useAuth();
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [checkedTerms, setCheckedTerms] = useState<boolean>(false);
  const [selectedFragments, setSelectedFragments] = useState<LidarFragment[]>(
    []
  );
  const [showModal, setShowModal] = useState(false);
  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const { showMessage } = useSnackbar();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Steps for the stepper
  const steps = [
    {
      label: t("pointcloud_export.step_one_label"),
      description: t("pointcloud_export.step_one_desc"),
    },
    {
      label: t("pointcloud_export.step_two_label"),
      description: t("pointcloud_export.step_two_desc"),
    },
    {
      label: t("pointcloud_export.step_three_label"),
      description: t("pointcloud_export.step_three_desc"),
    },
  ];

  // Effect to handle fragment selection limit
  useEffect(() => {
    if (selectedFragments.length === 0) {
      setActiveStep(0);
    }
  }, [selectedFragments]);

  // Handle PopOver close
  const handleClosePopOver = () => {
    setActiveStep(0);
    setSelectedFragments([]);
    if (!viewer) return;
    const mapcomponent: MapComponent = viewer.getComponent("map");
    mapcomponent.map.lidarLayer.clear_selection();
    if (!showMapWindow) {
      mapcomponent.deactivate();
    }
    changeShowPCLExport(false);
    changeShowStreetMap(false);
  };

  // Stepper navigation
  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setSelectedFragments([]);
      if (!viewer) return;
      const mapcomponent: MapComponent = viewer.getComponent("map");
      mapcomponent.map.lidarLayer.clear_selection();
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Handle terms checkbox click
  const handleClickTerms = (event: any) => {
    setCheckedTerms(event.target.checked);
  };

  // Form submit handler
  const onSubmit = (data: any) => {
    if (!viewer || !userData) return;
    monitor(
      apm,
      "ordered-fragments | PointCloudExport",
      () => {
        viewer
          .orderLidarFragments(
            data.email,
            userData.username,
            selectedFragments.map((x) => x.uid)
          )
          .then((resp: Response) => {
            if (resp.ok) {
              setShowModal(true);
            } else {
              throw new Error("Error in placing the order.");
            }
          })
          .catch((err: string) => {
            monitor(apm, "order-error | PointCloudExport", () => {
              showMessage(t("pointcloud_export.error_on_request"), "error");
            });
          });
      },
      { num_fragments: selectedFragments.length }
    );
  };

  // Initialize the map and interactions
  useEffect(() => {
    if (!viewer) return;
    const map_component: MapComponent = viewer.getComponent("map");
    map_component.map.olmap.on("moveend", function (event) {
      const view = event.map.getView();
      const zoom = view.getZoom();
      if (!zoom) return;
      setZoomLevel(zoom);
    });
    // If the map is not yet activated, activate it accordingly (Is the case in the API when hiding the map).
    if (!map_component.activated) {
      map_component.activate({
        container: "infra3dmap",
        locatorIcon:
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAAoCAYAAACM/rhtAAAABmJLR0QA/wD/AP+gvaeTAAADBklEQVRYw+2YX2iPURjHP+cls1/zZ/N3/m21GyYrFy4kuSDMdjH/c4MiSsxIUijJjUaK7YLIhcwW8n8uhFEkMVuKsLLNmsnGRvvZmp2vi/d98yNsNu/8Ln7fOhfvOed5zqfnPc9zzvvCP5SkMZLGElMPZK01kr7IVUI0Au6T7ZQ6v8paWxhtcGmSpIs7pZJcL4jKiBpASWelDmlVnLQEqb1Zkm5ES/TmS5KKd0iZSAuQTqz3o7gsGqJXqXcvpRVIq5HWIC1GqimXpOre+nd6CZcHZHB6O3wC4oEBQAdwaitAirV2T2/WML2AGwLUU3ktxK5sSAasN9gPeAPsLoLpKwDGG2Pq+jSCkvYBIQpy4R1uBDu91gI0AwUbgXYk7e/rxEiXJJ1cK61CunVIqn4ktbx1W225dKdAWmOkwqV+wkzry8Q4p9YP0sPTku3UH/WwyIXuYdkxPYCbBZQhC8YBa+H2EXhcAo2v3UlJKTA1B2bnQf+B+HMl5TiOcylowPvAdABe34P8OfCqDRKAkOcx7O3JVAPbrsPEeb75M2PM5MAAJS0HigGougl5cyAOGO0lR6T6Ae+9hDlwHqYs8kfWG2OOBQVYCWRAO6wbB02Nv4aLrBFNQHwIjtZBXCJAjTEmNagykwzAlcNQ3QUcXl0cDtSH4dJBv3d0kHXQxXl6FQZ1ARdpkejZRPoICNC9iDa+cBOiu4oHmqqgLew/BQbYigCpB/lv3XIDbUECvsUAQ1Phy19YtXs28Ql4p3QwgJIeAJA+1y0fTjdXaAImzvV9VAQZwRIAsjbDqAHwsQsPBvgMDAWyNvm9ZwIDdBynDCgjNBxyj38vxM5vPLcCdcDGAhiWBvDEcZyLgR511to0Y0wVAI9PQf5K9xWOwD1V/D3XCAwGthTCjA2++VRjTEWggN4+mgncBeBTA1zdC5UXoLnBnTB4JEzJguw9kDTBt8lyHKe0L69cKZKe/3C1CjdL4Y8/X7hqrbWT/udXXaaky5LqI6AaJJVaaxfG/rnEFFNMMcUU0x/1Dbk14fovKBg6AAAAAElFTkSuQmCC",
      });
    }

    if (showPCLExport) {
      if (map_component && projectConfig && tokens) {
        // Check if some features are present in the current extent
        map_component.map.olmap.once("postrender", () => {
          const extent = map_component.map.olmap.getView().calculateExtent();
          const url = new URL(
            `${ClientConfig.lidargateURL}fragments/${projectConfig.collection}?limit=1&boundary=${encodeURIComponent(`SRID=3857;POLYGON((${extent[0]} ${extent[1]}, ${extent[0]} ${extent[3]}, ${extent[2]} ${extent[3]}, ${extent[2]} ${extent[1]}, ${extent[0]} ${extent[1]}))`)}`
          ).href;
          changeLoading(true);
          fetch(url, {
            headers: {
              ...getAuthorizationHeaders(tokens.access_token),
              ...getApiKeyHeaders(),
            },
          })
            .then((res) => res.json())
            .then((res) => {
              changeLoading(false);
              if (res.value && res.value.length === 0) {
                changeAlertMessage({
                  message: t("pointcloud_export.no_fragments_in_extent"),
                  type: "info",
                });
              }
            });
        });

        map_component.map.setLidarMode(
          true,
          (selections: {
            added?: LidarFragment;
            removed?: LidarFragment;
            selection: LidarFragment[];
          }) => {
            if (selections.added) {
              const added: LidarFragment = selections.added;
              setSelectedFragments((prevSelected) => {
                const newSelected: LidarFragment[] = [...prevSelected, added];
                if (newSelected.length <= 10) {
                  return newSelected;
                } else {
                  map_component.map.lidarLayer.remove_from_selection(added.uid);
                  showMessage(
                    t("pointcloud_export.error_many_fragments"),
                    "warning"
                  );
                  return prevSelected;
                }
              });
            }
            if (selections.removed) {
              const removed: LidarFragment = selections.removed;
              setSelectedFragments((prevSelected) =>
                prevSelected.filter((a) => a.uid !== removed.uid)
              );
            }
            if (activeStep === 0) setActiveStep(1);
          },
          (_) => {},
          [204, 102, 51, 1.0], // selection color
          [204, 102, 51, 1.0] // highlight color
        );
      }
    } else {
      if (map_component) {
        map_component.map.setLidarMode(false);
      }
    }
  }, [showPCLExport]);

  const splitTextByPattern = (text: string, patterns: string[]): string[] => {
    const escapedPatterns = patterns.map((pattern) =>
      pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    );
    const regex = new RegExp(`(${escapedPatterns.join("|")})`, "gi");
    return text.split(regex);
  };
  const patterns = [
    "Besonderen Bedingungen",
    "Special Terms and Conditions",
    "Conditions Spéciales",
  ];
  const special_terms_parts = splitTextByPattern(
    t("pointcloud_export.accept_terms"),
    patterns
  );

  return (
    <>
      <Box>
        <PopoverElement
          visible={showPCLExport}
          handleClose={handleClosePopOver}
          title={t("pointcloud_export.title")}
        >
          <>
            {zoomLevel < 17 && (
              <Alert variant="outlined" severity="warning">
                {t("pointcloud_export.zoom_in_further")}
              </Alert>
            )}
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    classes={{
                      label: darkTheme
                        ? "infra3d-stepLabelDark"
                        : "infra3d-stepLabelLight",
                    }}
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <Typography
                        variant="body2"
                        marginBottom={"32px"}
                        color={darkTheme ? "white" : "darkblue.main"}
                      >
                        {step.description}
                      </Typography>
                      {activeStep === 1 && (
                        <TableContainer
                          component={Paper}
                          sx={{
                            backgroundColor: darkTheme
                              ? "darkblue.main"
                              : "white",
                            marginBottom: "32px",
                          }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    color: darkTheme
                                      ? "white"
                                      : "darkblue.main",
                                  }}
                                >
                                  {t("pointcloud_export.table_head_campaign")}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: darkTheme
                                      ? "white"
                                      : "darkblue.main",
                                  }}
                                >
                                  {t("pointcloud_export.table_head_date")}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: darkTheme
                                      ? "white"
                                      : "darkblue.main",
                                  }}
                                ></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedFragments.map((feature) => (
                                <TableRow
                                  key={feature.uid}
                                  sx={{
                                    "&:last-child td, &:last-child th": {
                                      border: 0,
                                    },
                                    "&:hover": {
                                      backgroundColor: "rgba(255, 103, 0, 0.2)",
                                    },
                                  }}
                                  onMouseOver={() => {
                                    if (!viewer) return;
                                    const mapcomponent: MapComponent =
                                      viewer.getComponent("map");
                                    mapcomponent.map.lidarLayer.highlight_feature(
                                      feature.uid
                                    );
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      color: darkTheme
                                        ? "white"
                                        : "darkblue.main",
                                    }}
                                  >
                                    {feature.campaign_name}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: darkTheme
                                        ? "white"
                                        : "darkblue.main",
                                    }}
                                  >
                                    {new Date(
                                      feature.start_timestamp
                                    ).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: darkTheme
                                        ? "white"
                                        : "darkblue.main",
                                    }}
                                  >
                                    <IconButton
                                      onClick={() => {
                                        setSelectedFragments((prevSelected) =>
                                          prevSelected.filter(
                                            (f) => f !== feature
                                          )
                                        );
                                        if (!viewer) return;
                                        const mapcomponent: MapComponent =
                                          viewer.getComponent("map");
                                        mapcomponent.map.lidarLayer.remove_from_selection(
                                          feature.uid
                                        );
                                      }}
                                    >
                                      <DeleteOutline
                                        sx={{
                                          color: darkTheme
                                            ? "white"
                                            : "darkblue.main",
                                        }}
                                      />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      {activeStep === 2 && (
                        <>
                          <TextField
                            defaultValue={userData?.usermail}
                            fullWidth
                            {...register("email", {
                              required: true,
                              pattern:
                                /^[A-Za-z0-9-!#$%&'*+-/=?^_`{|}~]+@[a-z0-9._-]+\.[A-Za-z]{2,3}$/,
                            })}
                            error={errors.email !== undefined ? true : false}
                            label={"Email"}
                            helperText={
                              errors.email &&
                              t("pointcloud_export.email_error_message")
                            }
                            sx={{
                              marginBottom: "32px",
                              "& .MuiInputBase-root": {
                                color: darkTheme ? "white" : "darkblue.main",
                              },
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: darkTheme
                                    ? "white"
                                    : "darkblue.main",
                                },
                                "&:hover fieldset": {
                                  borderColor: darkTheme
                                    ? "white"
                                    : "darkblue.main",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: darkTheme
                                    ? "white"
                                    : "darkblue.main",
                                },
                              },
                              "& .MuiInputLabel-root": {
                                color: darkTheme ? "white" : "darkblue.main",
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: darkTheme ? "white" : "darkblue.main",
                              },
                            }}
                          />
                          <Box
                            display="flex"
                            justifyContent="end"
                            marginBottom="32px"
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={checkedTerms}
                                  onChange={handleClickTerms}
                                  sx={{ color: darkTheme ? "white" : "black" }}
                                />
                              }
                              label=""
                            />
                            <Typography
                              variant="body2"
                              color={darkTheme ? "white" : "darkblue.main"}
                            >
                              {special_terms_parts.map((part, index) => {
                                if (patterns.includes(part)) {
                                  return (
                                    <Link
                                      key={index}
                                      style={{
                                        color: "#ff6700",
                                        textDecoration: "none",
                                        fontWeight: "600",
                                      }}
                                      rel="noreferrer"
                                      target="_blank"
                                      href="https://landing.infra3d.com/nutzungsbedingungen/besondere-bedingungen/punktwolken-export/"
                                    >
                                      {part}
                                    </Link>
                                  );
                                } else {
                                  return part;
                                }
                              })}
                            </Typography>
                          </Box>
                        </>
                      )}
                      <Box display="flex" justifyContent="end" gap="8px">
                        {activeStep !== 0 && (
                          <Button
                            variant="contained"
                            size="medium"
                            onClick={handleBack}
                          >
                            {t("pointcloud_export.button_back")}
                          </Button>
                        )}
                        {activeStep !== 0 && (
                          <Button
                            type={
                              index === steps.length - 1 ? "submit" : "button"
                            }
                            disabled={index === 2 && !checkedTerms}
                            variant="contained"
                            size="medium"
                            onClick={handleNext}
                          >
                            {index === steps.length - 1
                              ? t("pointcloud_export.button_finish")
                              : t("pointcloud_export.button_next")}
                          </Button>
                        )}
                      </Box>
                    </form>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </>
        </PopoverElement>
      </Box>

      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          handleClosePopOver();
        }}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            padding: "32px",
            borderRadius: "8px 16px 16px 8px",
            boxShadow: 3,
            outline: 0,
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <img src={PointcloudImage} width={"50%"} />

            <Typography
              id="modal-title"
              variant="h3"
              component="h3"
              mt={2}
              sx={{
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {t("pointcloud_export.title_message")}
            </Typography>

            <Typography
              id="modal-description-1"
              sx={{
                mt: 3,
                textAlign: "center",
                maxWidth: "80%",
              }}
            >
              {t("pointcloud_export.description_1")}
            </Typography>

            <Typography
              id="modal-description-1"
              sx={{
                mt: 3,
                textAlign: "center",
                maxWidth: "80%",
              }}
            >
              {t("pointcloud_export.description_2")}
            </Typography>
          </Box>

          <IconButton
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
            onClick={() => {
              setShowModal(false);
              handleClosePopOver();
            }}
          >
            <Close />
          </IconButton>
        </Paper>
      </Modal>
    </>
  );
}

export default PointCloudExport;
